import { buildImageSrc } from './formatUtils';
import { toObjectPosition } from './imagePosition';
import {
  type CardResource,
  isCalculatorResource,
  isCustomResource
} from './cms/types';

type ResolveCardImageInput = {
  article: CardResource;
  fallbackAlt?: string;
};

const cardTitle = (article: CardResource) => {
  if (isCalculatorResource(article)) return article.header;
  if (isCustomResource(article)) return article.custom_title;
  return article.title;
};

const pickPreviewImage = (article: ResolveCardImageInput['article']) => {
  if (!('preview_card_image' in article)) return null;
  return article.preview_card_image ?? null;
};

const pickPreviewImagePosition = (
  article: ResolveCardImageInput['article']
) => {
  if (!('preview_card_image_position' in article)) return null;
  return article.preview_card_image_position ?? null;
};

const getImageData = (
  article: ResolveCardImageInput['article'],
  previewImage: ReturnType<typeof pickPreviewImage>
) => {
  if (isCustomResource(article)) return article.custom_cover;
  return article.cover || previewImage;
};

const getImageAlt = (
  article: ResolveCardImageInput['article'],
  imageData: ReturnType<typeof getImageData>,
  fallbackAlt: string
) => {
  if (isCustomResource(article) && article.custom_alt_text) {
    return article.custom_alt_text;
  }

  if (imageData?.alternativeText) return imageData.alternativeText;
  const title = cardTitle(article);
  if (title) return title;
  return fallbackAlt;
};

export const resolveCardImage = ({
  article,
  fallbackAlt = 'Article cover image'
}: ResolveCardImageInput) => {
  const previewImage = pickPreviewImage(article);
  const previewImagePosition = pickPreviewImagePosition(article);

  /**
   * Noting for right now that we previously really only ever looked at `article.cover`
   * but there is actually a `preview_card_image` field on articles that is set for some
   * in prod. However when I reference the specific `preview_card_image` field here I noticed
   * that multiple articles share the same preview image:
   * so i am specifically opting to choose cover over preview_card_image for now unless we decide to change it.
   */
  const imageData = getImageData(article, previewImage);

  return {
    src: buildImageSrc(imageData?.url, ''),
    alt: getImageAlt(article, imageData, fallbackAlt),
    objectPosition: toObjectPosition(previewImagePosition)
  };
};
